import cn from 'utils/classnames'
import type { CompareCredit } from '../../../types/compare-credit'

/**
 * This component renders an editor star rating for a credit card/product
 *
 * It was coded as a rapid prototype for COM2-652. If this component
 * subsequently sees more use, we may wish to refine/refactor it (for example
 * by implementing logic to display other product types, adding an error
 * boundary with appropriate error handling including logging to Sentry and
 * fallback UI, etc). If not, we should consider removing it, along with the
 * `cardRatingEditorStar` Sanity schema type (defined in
 * `packages/cc-cms/schemas/card-rating-editor-star.ts`), and any other related
 * code
 *
 * @see https://linear.app/comparecredit/issue/COM2-652
 */
export const CardRatingEditorStar = ({
  referencedCards,
}: {
  referencedCards: null | Record<string, CompareCredit.Entity>
}) => {
  const CardRatingEditorStarRender: React.FC<{
    value: {
      card: {
        _id: string
        slug: string
      }
      cardLink?: boolean
      cardName?: boolean
      cardNameLink?: boolean
      cardRating?: boolean

      smStyling?: string
      mdStyling?: string
      lgStyling?: string
    }
  }> = ({ value: { card, smStyling, mdStyling, lgStyling } }) => {
    const product = referencedCards?.[card.slug]

    return (
      <div
        className={cn(
          'c-editor-star / max-w-xs / mb-4',
          { 'sm:float-left sm:mr-6': smStyling === 'smLeft' },
          { 'sm:float-right sm:ml-6': smStyling === 'smRight' },
          { 'md:float-left md:mr-6': mdStyling === 'mdLeft' },
          { 'md:float-right md:ml-6': mdStyling === 'mdRight' },
          { 'md:w-full md:float-none md:mx-0': mdStyling === 'mdFull' },
          { 'lg:float-left lg:mr-6': lgStyling === 'lgLeft' },
          { 'lg:float-right lg:ml-6': lgStyling === 'lgRight' },
          { 'lg:w-full lg:float-none lg:mx-0': lgStyling === 'lgFull' },
        )}
      >
        {product && product._type === 'card' ? (
          <div className="c-editor-star__inner / shadow rounded-lg px-2 py-4 sm:py-6 / flex justify-between items-center">
            <div className="c-editor-star__star / w-20">
              <div className="c-star w-20 h-20 relative / bg-gray-200">
                <div
                  className="c-star__mask / inline-block w-20 h-20 relative z-10 / bg-no-repeat bg-transparent bg-size-[100% 50%] bg-[url(/static/images/review-star-mask.png)]"
                  style={{ backgroundSize: '100% 100%' }}
                />
                <div
                  className={`c-star__fill-container / absolute bottom-0 w-full z-0`}
                  style={{
                    height: `${(product.expertReviewRating / 5) * 100}` + '%',
                  }}
                >
                  <div
                    className={`c-star__fill bg-tetriary / absolute bottom-0 w-full z-0`}
                    style={{
                      height: `${(product.expertReviewRating / 5) * 100}` + '%',
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="c-editor-star__content / shrink / w-2/3 pl-1 leading-tight">
              <p className="c-editor-star__title / uppercase text-gray-600 font-bold text-xs tracking-wider">
                Offer Rating
              </p>
              <p className="c-editor-star__number / font-serif leading-tight mb-1">
                <span className="c-editor-star__rating / text-primary-mid text-5xl">
                  {product.expertReviewRating}
                </span>
                <span className="c-editor-star__five / text-gray-600 text-3xl">
                  / 5
                </span>
              </p>
              <p className="c-editor-star__footnote / uppercase text-gray-600 font-bold text-xs tracking-wider">
                By Our Content&nbsp;Team
              </p>
            </div>
          </div>
        ) : (
          <div className="c-content-placeholder-bg / w-full / mb-1 / pt-[9rem] / bg-gray-200 rounded"></div>
        )}
      </div>
    )
  }
  return CardRatingEditorStarRender
}
